import { StageTransitionCreationRequest, StageTransitionDetail } from '@portal/pkg-api/gen/stages'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { StagesAdminApi } from '@portal/pkg-api/admin'
import { data } from '@portal/pkg-api/shared'
import { idArrayToIndexMap } from '@portal/pkg-utils'
import { StageQueries } from '@admin/data/Stages'
import { PostEntityCreationRuleQueries } from '@admin/data/PostEntityCreationRules'

const prefix = 'admin.transitions'

export const TransitionQueries = {
  initialStageTransitions: (entityType: number) => {
    if (entityType == null) throw new Error('entityType is required for initialStageTransitions')
    return {
      queryKey: [`${prefix}.initialStageTransitions`, entityType],
      queryFn: () => data(StagesAdminApi.getInitialStageTransitions(entityType)),
    }
  },
  transitions: (entityType?: number) => ({
    queryKey: [`${prefix}.transitions`, entityType],
    queryFn: () => data(StagesAdminApi.getTransitions(entityType)),
  }),
}

export function useInitialStageTransitions(entityType: number) {
  return useQuery(TransitionQueries.initialStageTransitions(entityType))
}

export const newEmptyStageTransitionDetail = {
  id: -1,
  name: '',
  source: -1,
  target: -1,
  sortIndex: 1,
  enabled: true,
  completionActions: [],
  approverRequiredCount: 1,
  noteRequired: false,
  roles: [],
  rules: [],
  requiredFields: [],
}

export function useTransitions(entityType?: number) {
  return useQuery(TransitionQueries.transitions(entityType))
}

export function useSaveTransition(entityType: number) {
  const queryClient = useQueryClient()
  return useMutation<StageTransitionDetail, unknown, StageTransitionDetail>({
    mutationFn: async (transition) => {
      if (transition.id > 0) {
        return await data(StagesAdminApi.updateTransition(transition.id, transition))
      } else {
        const newTransition: StageTransitionCreationRequest = {
          name: transition.name,
          source: transition.source,
        }
        return await data(StagesAdminApi.createTransition(newTransition))
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries(StageQueries.stages(entityType!))
      void queryClient.invalidateQueries(TransitionQueries.transitions(entityType))
      void queryClient.invalidateQueries(PostEntityCreationRuleQueries.rules(entityType))
    },
  })
}

export const queryReorderTransitions = async (transitions: StageTransitionDetail[]) => {
  const reorderMap = idArrayToIndexMap(transitions)
  return await data(StagesAdminApi.orderTransitions(reorderMap))
}

export function useReorderTransitions(entityType?: number) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (transitions: StageTransitionDetail[]) => {
      const reorderMap = idArrayToIndexMap(transitions)
      return data(StagesAdminApi.orderTransitions(reorderMap))
    },
    onSuccess: () => {
      void queryClient.invalidateQueries(TransitionQueries.transitions(entityType))
      void queryClient.invalidateQueries(StageQueries.stages(entityType!))
    },
  })
}

export function useDeleteTransition(entityType?: number) {
  const queryClient = useQueryClient()
  return useMutation<StageTransitionDetail, unknown, StageTransitionDetail>({
    mutationFn: async (transition) => {
      await StagesAdminApi.deleteTransition(transition.id)
      return transition
    },
    onSuccess: () => {
      void queryClient.invalidateQueries(StageQueries.stages(entityType!))
      void queryClient.invalidateQueries(TransitionQueries.transitions(entityType))
    },
  })
}
